import { useState } from 'react';
import LoiGeneratorDialog, { type LoiPrefill } from './LoiGeneratorDialog';
import { usePageActionFallback, type PageActionFields } from '../contexts/PageActionContext';

/**
 * THE LOI DIALOG WITH NO RECORD BEHIND IT - his requirement of 2026-09-21.
 *
 * ⭐⭐⭐ WHY THIS EXISTS. The assistant could already open the LOI dialog, but only from a
 *    property or deal page, so the app refused exactly when he needed it most: pricing a new OM,
 *    before anything is a record. He said plainly what that costs - *"if not, im going to end up
 *    continuing doing it here or in claude cowork and not use the app at all."*
 *
 * ⭐⭐ IT TAKES NOTHING AWAY. `LoiGeneratorDialog` already treats every prop but `open` as
 *    optional, and `POST /api/loi/generate` already takes `deal_id` as optional - measured in
 *    the 96th, where driving it with no deal returned `filed: false` and wrote nothing. This is
 *    a missing ENTRY POINT, not a missing capability.
 *
 * ⛔⛔ AND IT KEEPS THE GUARANTEE BY CONSTRUCTION. The rule that protects every document surface
 *    here is that the model never names a record, so it cannot aim one at the wrong building
 *    (`fileToTarget`: a Victorville counter aimed at a Hesperia property). A recordless letter
 *    has no target at all - there is nothing to aim. Nothing in this file may ever grow a
 *    `propertyId` or `dealId` prop; the moment it does, that argument is gone.
 *
 * ⚠️ MOUNTED BY `Layout`, NEVER BY `AiRail`. The rail renders nothing while collapsed, so a host
 *   inside it would silently deregister when he closed the panel and the action would refuse
 *   for a reason invisible from the screen.
 */
export default function RecordlessLoiHost() {
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState<LoiPrefill>({});

  usePageActionFallback('open_loi_dialog', (fields: PageActionFields) => {
    /**
     * ⭐ The field names already match `LoiPrefill`, which is why `PageActionFields` was given
     * one flat shape - the handler spreads what its own dialog understands and ignores the rest.
     * ⛔ Only what he STATED arrives here: `appAssistantActions` drops anything the model did not
     * take from his own words, so a blank stays blank rather than becoming a plausible guess.
     */
    setPrefill(fields as LoiPrefill);
    setOpen(true);
  });

  /**
   * ⛔ NO `dealId`, DELIBERATELY - and no `onFiled`. With a deal the dialog FILES into the Deal
   *   Room; without one it generates and hands the letter over, which is the whole point here.
   * ⭐ `recordless`: a change the chat asks for with no record on screen (`revise_open_loi`) looks in the rail's own
   *   map, so this letter answers it there - never a page's letter, and a page's ask never reaches this one.
   */
  return <LoiGeneratorDialog open={open} onClose={() => setOpen(false)} prefill={prefill} recordless />;
}
